import { forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes, createContext, useContext } from "react";
import { cn } from "../lib/cn";

interface TableContextValue {
  compact?: boolean;
  stickyHeader?: boolean;
}

const TableContext = createContext<TableContextValue>({});

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  compact?: boolean;
  stickyHeader?: boolean;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, compact, stickyHeader, ...props }, ref) => (
    <TableContext.Provider value={{ compact, stickyHeader }}>
      <div className="relative w-full overflow-x-auto rounded-lg border border-border">
        <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
      </div>
    </TableContext.Provider>
  ),
);
Table.displayName = "Table";

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const { stickyHeader } = useContext(TableContext);
  return (
    <thead ref={ref} className={cn("bg-muted/50 [&_tr]:border-b", stickyHeader && "sticky top-0 z-10 bg-card", className)} {...props} />
  );
});
TableHeader.displayName = "TableHeader";

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
));
TableBody.displayName = "TableBody";

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-border transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

export const TableHead = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { compact } = useContext(TableContext);
  return (
  <th
    ref={ref}
    className={cn(
      "text-left align-middle font-semibold uppercase tracking-wide text-muted-foreground",
      compact ? "h-auto py-2 px-3 text-xs" : "h-11 px-4 text-xs",
      className,
    )}
    {...props}
  />
)});
TableHead.displayName = "TableHead";

export const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { compact } = useContext(TableContext);
  return (
  <td ref={ref} className={cn("align-middle", compact ? "py-2 px-3 text-xs" : "px-4 py-3", className)} {...props} />
)});
TableCell.displayName = "TableCell";
